/**
 * Sidebar — cột 256px nền đậm (bg-sidebar cả 2 theme), nhóm epic uppercase,
 * NavLink active bg-sidebar-active-bg, badge hàng đợi, footer user. US-AE.F0-0000-S04.
 *
 * US-AE-IMP2.T02 (@UC9136):
 *  - Active chính xác: item prefix-lồng (vd '/billing' của '/billing/usage') dùng NavLink `end`
 *    → KHÔNG active nhầm khi ở route con của item khác (navItemNeedsEnd).
 *  - Collapse/expand: rail icon-only 64px ↔ 256px; persist localStorage (lib/sidebar); tooltip
 *    khi collapsed. Chỉ animate `width` container (layout-bound) — chấp nhận cho toggle sidebar
 *    (không phải motion tần suất cao), nội dung dùng opacity/transform-friendly ẩn/hiện nhãn.
 *
 * S03 — accordion per-group: khi sidebar mở rộng, mỗi nhóm epic có header là <button>
 *  (aria-expanded + aria-controls) click để gập/mở danh sách item của nhóm; persist từng nhóm
 *  qua lib/sidebar-groups. Khi rail icon-only thì KHÔNG áp accordion (item vẫn hiện icon như cũ).
 */
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, PanelLeftClose, PanelLeftOpen, ChevronDown } from "lucide-react";
import { canViewNav, type AdminRole } from "@goppy/contracts";
import { NAV, navItemNeedsEnd } from "./nav";
import { cn } from "@/lib/utils";
import { getCurrentAdmin, logout } from "@/lib/auth.js";
import { useRbacMatrix } from "@/features/rbac-matrix/use-rbac-matrix.js";
import { resolveInitialCollapsed, persistSidebarCollapsed } from "@/lib/sidebar.js";
import { resolveCollapsedGroups, persistCollapsedGroup } from "@/lib/sidebar-groups.js";
import { useQueueSummary } from "@/features/dashboard/use-dashboard.js";
import type { AdminQueueSummary } from "@goppy/contracts";

/** Slug an toàn cho id DOM (aria-controls) từ labelKey (vd 'g.ae12b' → 'g-ae12b'). */
function groupPanelId(labelKey: string): string {
  return `nav-group-${labelKey.replace(/[^a-zA-Z0-9]+/g, "-")}`;
}

/** Nhãn vai trò i18n (least-privilege: role undefined/lạ → nhãn trung tính). */
const ROLE_LABEL_KEY: Record<AdminRole, string> = {
  SUPER_ADMIN: "ui.role_super",
  OPS: "ui.role_ops",
  SUPPORT: "ui.role_support",
  READ_ONLY: "ui.role_readonly",
};

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  // Collapse state init từ localStorage (khôi phục qua reload). Toggle persist ngay.
  const [collapsed, setCollapsed] = useState<boolean>(resolveInitialCollapsed);
  // S03 — group nào đang GẬP (accordion), init từ localStorage; toggle persist ngay.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(resolveCollapsedGroups);
  const admin = getCurrentAdmin();
  const roleLabel = admin?.role ? t(ROLE_LABEL_KEY[admin.role]) : t("ui.role_unknown");
  // Badge hàng đợi đọc THẬT từ /admin/queues/summary (thay QUEUE_COUNTS hardcode). Chưa có → không badge.
  const { data: queues } = useQueueSummary();

  // @UC9002 — lọc nav theo ma trận RBAC-UI: ẩn item vai hiện tại KHÔNG được xem (mirror @Roles server;
  // server vẫn chốt thật). Nhóm rỗng sau lọc → ẩn luôn tiêu đề nhóm. Fail-safe: key chưa khai → hiện.
  const rbacMatrix = useRbacMatrix();
  const role = admin?.role as AdminRole | undefined;
  const visibleNav = NAV.map((group) => ({
    ...group,
    items: group.items.filter((it) => canViewNav(rbacMatrix, role, it.key)),
  })).filter((group) => group.items.length > 0);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      persistSidebarCollapsed(next);
      return next;
    });
  }

  // S03 — gập/mở một nhóm epic; persist rồi trả về Set mới (immutable).
  function toggleGroup(labelKey: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      const nowCollapsed = !next.has(labelKey);
      if (nowCollapsed) next.add(labelKey);
      else next.delete(labelKey);
      persistCollapsedGroup(labelKey, nowCollapsed);
      return next;
    });
  }

  async function handleLogout() {
    if (signingOut) return;
    setSigningOut(true);
    // logout luôn resolve (best-effort BE + clear local) → điều hướng /login sau đó.
    await logout();
    navigate("/login", { replace: true });
  }

  const toggleLabel = collapsed ? t("ui.expand_sidebar") : t("ui.collapse_sidebar");
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <aside
      data-collapsed={collapsed ? "true" : "false"}
      className={cn(
        "sticky top-0 flex h-screen shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-fg transition-[width] duration-200",
        collapsed ? "w-16" : "w-64",
      )}
    >
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-2.5 border-b border-sidebar-border px-[18px]",
          collapsed && "justify-center px-0",
        )}
      >
        {/* Khi thu gọn (rail): CHỈ hiện nút toggle (để mở lại được — logo ẩn cho gọn). */}
        {collapsed ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={toggleLabel}
            aria-pressed={collapsed}
            title={toggleLabel}
            className="rounded p-1.5 text-sidebar-fg-sub transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ToggleIcon className="h-[16px] w-[16px]" />
          </button>
        ) : (
          <>
            <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#3a86d9] to-[#0b5cab] text-[15px] font-bold text-white">
              G
            </div>
            <div className="flex min-w-0 flex-col leading-[1.15]">
              <span className="text-[14px] font-semibold text-white">Goppy</span>
              <span className="truncate text-[11px] text-sidebar-fg-sub">{t("ui.console")}</span>
            </div>
            {/* US-AE-IMP2.T02b: nút collapse toàn sidebar chuyển LÊN header (cạnh logo), đẩy phải. */}
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={toggleLabel}
              aria-pressed={collapsed}
              title={toggleLabel}
              className="ml-auto rounded p-1.5 text-sidebar-fg-sub transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ToggleIcon className="h-[16px] w-[16px]" />
            </button>
          </>
        )}
      </div>

      <nav
        aria-label={t("ui.nav_main")}
        className="gp-scroll flex-1 overflow-y-auto px-2.5 pb-5 pt-2.5"
      >
        {visibleNav.map((group) => {
          const groupName = t(group.labelKey);
          const panelId = groupPanelId(group.labelKey);
          // Accordion CHỈ áp khi sidebar mở rộng; rail icon-only luôn hiện item (như cũ).
          const groupCollapsed = !collapsed && collapsedGroups.has(group.labelKey);
          return (
          <div key={group.labelKey} className="mt-3.5">
            {!collapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group.labelKey)}
                aria-expanded={!groupCollapsed}
                aria-controls={panelId}
                aria-label={t(groupCollapsed ? "ui.expand_group" : "ui.collapse_group", {
                  name: groupName,
                })}
                className="flex w-full items-center justify-between gap-2 rounded-sm px-2.5 pb-1.5 pt-0.5 text-[10.5px] font-semibold uppercase tracking-[.06em] text-sidebar-fg-sub transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="truncate">{groupName}</span>
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-150",
                    groupCollapsed && "-rotate-90",
                  )}
                />
              </button>
            )}
            <div id={panelId} hidden={groupCollapsed}>
            {group.items.map((it) => {
              const Icon = it.icon;
              // badgeKey (disputes/corrections/risk/gdpr/drift/abuse) khớp key AdminQueueSummary.
              const badgeCount =
                it.badgeKey && queues ? queues[it.badgeKey as keyof AdminQueueSummary] : undefined;
              // Ẩn badge khi 0 hoặc chưa có dữ liệu (không hiện "0" gây rối).
              const badge = badgeCount && badgeCount > 0 ? badgeCount : undefined;
              const label = t(it.key);
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  // end cho '/' và item prefix-lồng (vd '/billing') → active chính xác segment.
                  end={it.to === "/" || navItemNeedsEnd(it.to)}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    cn(
                      "my-px flex h-9 w-full items-center gap-2 rounded-sm px-2.5 text-[13px] transition-colors",
                      collapsed ? "justify-center" : "justify-between",
                      isActive
                        ? "bg-sidebar-active-bg font-semibold text-sidebar-active-fg"
                        : "font-normal text-sidebar-fg hover:bg-white/5",
                    )
                  }
                >
                  <span
                    className={cn(
                      "flex min-w-0 items-center gap-2.5",
                      collapsed && "justify-center",
                    )}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0 opacity-90" />
                    {!collapsed && <span className="truncate">{label}</span>}
                  </span>
                  {badge && !collapsed ? (
                    <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-tone-danger-bg px-1.5 text-[10.5px] font-bold text-tone-danger-fg">
                      {badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
            </div>
          </div>
          );
        })}
      </nav>

      <div
        className={cn(
          "flex shrink-0 items-center gap-2.5 border-t border-sidebar-border p-3",
          collapsed && "flex-col gap-2 p-2",
        )}
      >
        <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-[12px] font-semibold text-white">
          {roleLabel.slice(0, 1).toUpperCase()}
        </div>
        {!collapsed && (
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            {/* @UC9135 (S02): định danh THẬT — email admin đang đăng nhập; edge chỉ có id → "Admin"
                trung tính; chưa auth → "—". KHÔNG bao giờ hiển thị `#<uuid-slice>` (bug cũ). */}
            <span
              data-testid="admin-identity"
              title={admin?.email}
              className="truncate text-[13px] font-medium text-[#e8edf3]"
            >
              {admin ? (admin.email ?? "Admin") : "—"}
            </span>
            <span className="text-[11px] text-sidebar-fg-sub">{roleLabel}</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => void handleLogout()}
          disabled={signingOut}
          aria-label={t("ui.logout")}
          title={t("ui.logout")}
          className="rounded p-1 text-sidebar-fg-sub transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <LogOut className="h-[15px] w-[15px]" />
        </button>
      </div>
    </aside>
  );
}
